import * as React from 'react';

import { Button, Grid, List, ListItem, ListItemIcon, Spacer, Typography } from '@emburse/ui-core';
import { CheckIcon, Icon } from '@emburse/ui-essentials';

import { useActivationPlanPricing } from '@ReactHooks/useActivationPlanPricing';
import { AMEX_BILLING_STRIPE_PLAN_TYPE } from '@Shared/constants';
import { getActivationPlanMinimumSummary, getActivationPlanPerUserAmount } from '@Shared/helpers';
import { ICompanyModel } from '@Shared/models';

import { ListItemWithCheckbox } from './SignUpSpendPlans';

import * as style from './SignUpPlanSelection.styl';

interface ISignUpAmexPlans {
  handleSelectPlan: (billing_plan: ICompanyModel['billing_plan']) => void;
  basicButtonText?: string;
  disableBasicButton?: boolean;
}

export const SignUpAmexPlans: React.FC<ISignUpAmexPlans> = ({
  handleSelectPlan,
  basicButtonText = 'Select',
  disableBasicButton = false
}) => {
  const currencyCode = window.ORG?.currency_code || 'USD';
  const { pricingDetails: starterPlanPricing, pricingLoading: starterPlanPricingLoading } = useActivationPlanPricing(
    AMEX_BILLING_STRIPE_PLAN_TYPE.FREE_PLAN
  );
  const { pricingDetails: proPlanPricing, pricingLoading: proPlanPricingLoading } = useActivationPlanPricing(
    AMEX_BILLING_STRIPE_PLAN_TYPE.PRO_PLAN
  );
  const starterPlanPrice = getActivationPlanPerUserAmount(starterPlanPricing, currencyCode, 'Free');
  const proPlanPrice = getActivationPlanPerUserAmount(proPlanPricing, currencyCode);
  const proPlanMinimumCopy = getActivationPlanMinimumSummary(proPlanPricing, currencyCode);

  return (
    <Grid container direction={'row'} spacing={2} className={style.global}>
      <Grid item sm={6}>
        <div className="cardStyle">
          <Typography variant="h5">Starter</Typography>
          <Spacer size={24} />
          {starterPlanPricingLoading ? (
            <Typography variant="body2">Loading pricing...</Typography>
          ) : (
            <Typography variant="h6" className="flex" style={{ alignItems: 'baseline' }}>
              {starterPlanPrice || 'Free'}
              <Typography variant="caption">/unlimited users</Typography>
            </Typography>
          )}
          <Spacer size={24} />
          <div className="flex jc-c">
            <Button
              style={{ width: '100%' }}
              onClick={() => handleSelectPlan(AMEX_BILLING_STRIPE_PLAN_TYPE.FREE_PLAN)}
              data-testid="amex_starter_free_stripe"
              disabled={disableBasicButton}
              className="amex-starter"
            >
              {basicButtonText}
            </Button>
          </div>
          <Spacer size={24} />
          <List>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              American Express card feeds (Small business, Corporate and Purchasing)
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              Unlimited American Express virtual card issuing
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              Unlimited expense policies and approval
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              All accounting syncs and custom export (email automation supported)
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              Unlimited reimbursements (mileage tracking)
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              SSO and admin controlled 2FA
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              Mobile app (iOS and Android)
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Icon iconUrl={CheckIcon} />
              </ListItemIcon>
              End user and admin support
            </ListItem>
          </List>
        </div>
      </Grid>
      <Grid item sm={6}>
        <div className="cardStyle">
          <Typography variant="h5">Plus</Typography>
          <Spacer size={24} />
          {proPlanPricingLoading ? (
            <Typography variant="body2">Loading pricing...</Typography>
          ) : (
            <>
              <Typography variant="h6" className="flex" style={{ alignItems: 'baseline' }}>
                {proPlanPrice || 'Pricing unavailable'}
                {proPlanPrice && <Typography variant="caption">/user per month&nbsp;</Typography>}
              </Typography>
              <Typography variant="caption">
                {proPlanMinimumCopy ? `(Includes free 30 day trial, ${proPlanMinimumCopy})` : '(Includes free 30 day trial)'}
              </Typography>
            </>
          )}
          <Spacer size={24} />
          <div className="flex jc-c">
            <Button
              style={{ width: '100%' }}
              onClick={() => handleSelectPlan(AMEX_BILLING_STRIPE_PLAN_TYPE.PRO_PLAN)}
              data-testid="amex_professional_six"
              className="amex-professional"
            >
              Select
            </Button>
          </div>
          <Spacer size={24} />
          <List>
            <ListItemWithCheckbox sx={{ fontWeight: 600 }}>Everything in Starter, +</ListItemWithCheckbox>
            <ListItemWithCheckbox>Salesforce API</ListItemWithCheckbox>
            <ListItemWithCheckbox>Amazon Business Integration</ListItemWithCheckbox>
            <ListItemWithCheckbox>
              <div>
                Multi-Subsidiary Support*<sup>, </sup>**
              </div>
            </ListItemWithCheckbox>
            <ListItemWithCheckbox>SFTP Accounting Automation*</ListItemWithCheckbox>
            <ListItemWithCheckbox>REST API Access*</ListItemWithCheckbox>
          </List>
          <Typography variant="caption">*Requires guided implementation</Typography>
          <Spacer size={8} />
          <Typography variant="caption">**Each subsidiary is billed separately based on usage</Typography>
        </div>
      </Grid>
    </Grid>
  );
};
